import React, {useState} from 'react'
import styled from '@emotion/styled'
import {createPortal} from 'react-dom'
import {theme} from '../../accountManagement/theme'

// ─── Styled Components ────────────────────────────────────────────────────────

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: ${theme.zIndex.modal};
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.55);
  padding: ${theme.space[4]};
  animation: fadeIn ${theme.transitions.normal} forwards;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`

const Dialog = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 520px;
  border-radius: ${theme.radii.xl};
  overflow: hidden;
  box-shadow: ${theme.shadows.lg};
  animation: slideIn ${theme.transitions.slow} cubic-bezier(0.22, 1, 0.36, 1)
    forwards;

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(14px) scale(0.98);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
`

// Dark navy header section
const Header = styled.div`
  background-color: #1e2d6b;
  padding: ${theme.space[6]};
  display: flex;
  flex-direction: column;
  gap: ${theme.space[3]};
`

const Eyebrow = styled.span`
  font-family: ${theme.fonts.openSans};
  font-size: ${theme.fontSizes.xs};
  font-weight: ${theme.fontWeights.semibold};
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.6);
`

const Headline = styled.h2`
  margin: 0;
  font-family: ${theme.fonts.openSans};
  font-size: ${theme.fontSizes['3xl']};
  font-weight: ${theme.fontWeights.bold};
  color: #ffffff;
  line-height: 1.25;
`

const WarningBanner = styled.div`
  display: flex;
  gap: ${theme.space[3]};
  background-color: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: ${theme.radii.lg};
  padding: ${theme.space[4]};
`

const WarningIcon = styled.span`
  flex-shrink: 0;
  font-size: 16px;
  line-height: 1.5;
`

const WarningText = styled.p`
  margin: 0;
  font-family: ${theme.fonts.openSans};
  font-size: ${theme.fontSizes.sm};
  font-weight: ${theme.fontWeights.regular};
  color: rgba(255, 255, 255, 0.85);
  line-height: 1.6;
`

// White body section
const Body = styled.div`
  background-color: ${theme.colors.bg.primary};
  padding: ${theme.space[6]};
  display: flex;
  flex-direction: column;
  gap: ${theme.space[3]};
`

const Question = styled.p`
  margin: 0;
  font-family: ${theme.fonts.openSans};
  font-size: ${theme.fontSizes.base};
  font-weight: ${theme.fontWeights.semibold};
  color: ${theme.colors.text.primary};
`

const OptionCard = styled.button`
  display: flex;
  align-items: flex-start;
  gap: ${theme.space[4]};
  padding: ${theme.space[4]};
  border-radius: ${theme.radii.lg};
  border: 2px solid
    ${({isSelected, isYes}) =>
      isSelected
        ? isYes
          ? '#16A085'
          : theme.colors.status.danger.border
        : theme.colors.border.default};
  background-color: ${({isSelected, isYes}) =>
    isSelected
      ? isYes
        ? 'rgba(22, 160, 133, 0.06)'
        : theme.colors.status.danger.bg
      : theme.colors.bg.primary};
  cursor: pointer;
  text-align: left;
  width: 100%;
  transition: border-color ${theme.transitions.fast},
    background-color ${theme.transitions.fast};

  &:hover {
    border-color: ${({isYes}) =>
      isYes ? '#16A085' : theme.colors.status.danger.border};
    background-color: ${({isYes}) =>
      isYes ? 'rgba(22, 160, 133, 0.04)' : theme.colors.status.danger.bg};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${theme.shadows.focus};
  }
`

const OptionIndicator = styled.div`
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  border-radius: ${theme.radii.full};
  border: 2px solid
    ${({isSelected, isYes}) =>
      isSelected
        ? isYes
          ? '#16A085'
          : theme.colors.status.danger.text
        : theme.colors.border.strong};
  background-color: ${({isSelected, isYes}) =>
    isSelected
      ? isYes
        ? '#16A085'
        : theme.colors.status.danger.text
      : 'transparent'};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 2px;
  transition: border-color ${theme.transitions.fast},
    background-color ${theme.transitions.fast};
`

const CheckIcon = () => (
  <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
    <path
      d="M1 4L3.5 6.5L9 1"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const XIcon = () => (
  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
    <path
      d="M1 1L9 9M9 1L1 9"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
)

const OptionContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.space[1]};
`

const OptionTitle = styled.span`
  font-family: ${theme.fonts.openSans};
  font-size: ${theme.fontSizes.base};
  font-weight: ${theme.fontWeights.semibold};
  color: ${theme.colors.text.primary};
`

const OptionDescription = styled.span`
  font-family: ${theme.fonts.openSans};
  font-size: ${theme.fontSizes.sm};
  font-weight: ${theme.fontWeights.regular};
  color: ${theme.colors.text.secondary};
  line-height: 1.55;
`

// Footer
const Footer = styled.div`
  background-color: ${theme.colors.bg.secondary};
  border-top: 1px solid ${theme.colors.border.default};
  padding: ${theme.space[4]} ${theme.space[6]};
  display: flex;
  justify-content: flex-end;
  gap: ${theme.space[3]};
`

const ButtonBase = styled.button`
  font-family: ${theme.fonts.openSans};
  font-size: ${theme.fontSizes.base};
  font-weight: ${theme.fontWeights.semibold};
  border-radius: ${theme.radii.full};
  padding: ${theme.space[2.5]} ${theme.space[5]};
  cursor: pointer;
  transition: background-color ${theme.transitions.fast},
    border-color ${theme.transitions.fast}, color ${theme.transitions.fast};
  white-space: nowrap;

  &:focus-visible {
    outline: none;
    box-shadow: ${theme.shadows.focus};
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`

const SecondaryButton = styled(ButtonBase)`
  background-color: transparent;
  border: 1px solid ${theme.colors.border.strong};
  color: ${theme.colors.text.primary};

  &:hover:not(:disabled) {
    background-color: ${theme.colors.bg.tertiary};
    border-color: ${theme.colors.border.strong};
  }
`

const PrimaryButton = styled(ButtonBase)`
  background-color: #1e2d6b;
  border: 1px solid #1e2d6b;
  color: #ffffff;

  &:hover:not(:disabled) {
    background-color: #162258;
    border-color: #162258;
  }
`

// ─── Component ────────────────────────────────────────────────────────────────

export const RETAIN_WEBSITE_MODAL_STATES = {
  CLOSE: 'close',
  OPEN: 'open',
  ANSWERED_YES: 'answered_yes',
  ANSWERED_NO: 'answered_no',
}

/**
 * Proposal-Tool-styled downgrade modal asking whether to retain the
 * Professionally Designed Website as a paid add-on.
 *
 * Props:
 *   isOpen         {boolean}   — controls visibility
 *   downgradedFrom {string}    — plan name, e.g. "Thryv Signature"
 *   onConfirm      {function}  — called with 'yes' | 'no' | null (back)
 */
export function RetainWebsiteDowngradeModal({
  isOpen,
  downgradedFrom,
  onConfirm,
}) {
  const [selected, setSelected] = useState(null)

  if (!isOpen) return null

  const handleConfirm = () => {
    if (!selected) return
    onConfirm(selected)
    setSelected(null)
  }

  const planName = downgradedFrom ?? 'Thryv Signature'

  return createPortal(
    <Backdrop>
      <Dialog
        role="dialog"
        aria-modal="true"
        aria-label="Retain Professionally Designed Website"
      >
        <Header>
          <Eyebrow>Plan Downgrade</Eyebrow>
          <Headline>Retain Professionally Designed Website?</Headline>
          <WarningBanner>
            <WarningIcon>⚠️</WarningIcon>
            <WarningText>
              This account is being downgraded from the{' '}
              <strong>{planName}</strong> plan. The Professionally Designed
              Website included with {planName} will be removed and the client
              will lose their website.
            </WarningText>
          </WarningBanner>
        </Header>

        <Body>
          <Question>
            Does the client want to retain their Professionally Designed
            Website?
          </Question>

          <OptionCard
            type="button"
            isYes
            isSelected={selected === 'yes'}
            onClick={() => setSelected('yes')}
          >
            <OptionIndicator isYes isSelected={selected === 'yes'}>
              {selected === 'yes' && <CheckIcon />}
            </OptionIndicator>
            <OptionContent>
              <OptionTitle>Yes — Add Paid Website</OptionTitle>
              <OptionDescription>
                The paid Professionally Designed Website add-on is automatically
                added to the order. The design fee should not be charged since
                the website already exists.
              </OptionDescription>
            </OptionContent>
          </OptionCard>

          <OptionCard
            type="button"
            isYes={false}
            isSelected={selected === 'no'}
            onClick={() => setSelected('no')}
          >
            <OptionIndicator isYes={false} isSelected={selected === 'no'}>
              {selected === 'no' && <XIcon />}
            </OptionIndicator>
            <OptionContent>
              <OptionTitle>No — Remove Website</OptionTitle>
              <OptionDescription>
                The order proceeds with the downgrade and the Professionally
                Designed Website entitlement is removed.
              </OptionDescription>
            </OptionContent>
          </OptionCard>
        </Body>

        <Footer>
          <SecondaryButton type="button" onClick={() => onConfirm(null)}>
            Back
          </SecondaryButton>
          <PrimaryButton
            type="button"
            disabled={!selected}
            onClick={handleConfirm}
          >
            {selected === 'yes'
              ? 'Yes — Add Paid Website →'
              : selected === 'no'
              ? 'No — Remove Website →'
              : 'Confirm →'}
          </PrimaryButton>
        </Footer>
      </Dialog>
    </Backdrop>,
    document.body,
  )
}
